/**
 * English spell checking over a Hunspell dictionary (nspell), loaded on demand.
 * Finds misspelled words with their character offsets and ranked suggestions.
 */

import affixUrl from "typo-js/dictionaries/en_US/en_US.aff";
import wordsUrl from "typo-js/dictionaries/en_US/en_US.dic";

const MIN_WORD_LENGTH = 3;
const MAX_SUGGESTIONS = 5;

const TRANSPOSITION_COST = 0.9;
const FIRST_LETTER_PENALTY = 0.5;
const CAPITALIZATION_PENALTY = 0.2;

const COMBINING_MARKS = /[\u0300-\u036f]/g;
const CURLY_APOSTROPHE = /\u2019/g;

const NON_LANGUAGE_PATTERNS = [
  /[a-z][a-z\d+.-]*:\/\/\S+/gi,
  /\bwww\.\S+/gi,
  /\S+@\S+\.\S+/g,
  /(?:^|\s)@[\w.-]+/g,
  /\b[\w-]+\.(?:com|net|org|io|gov|edu|co|us|info|biz)\b\S*/gi,
];

let spellerPromise = null;

async function fetchText(url) {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Failed to load ${url} (${response.status})`);
  return response.text();
}

export function loadSpeller() {
  if (!spellerPromise) {
    spellerPromise = Promise.all([
      import("nspell"),
      fetchText(affixUrl),
      fetchText(wordsUrl),
    ])
      .then(([module, aff, dic]) => (module.default || module)({ aff, dic }))
      .catch((error) => {
        spellerPromise = null;
        throw error;
      });
  }

  return spellerPromise;
}

export function foldAccents(word) {
  return word.normalize("NFD").replace(COMBINING_MARKS, "");
}

function isCheckable(word) {
  if (word.length < MIN_WORD_LENGTH) return false;
  return word[0] === word[0].toLowerCase();
}

export function isSpelledCorrectly(speller, word) {
  const variants = new Set();

  [word, foldAccents(word)].forEach((form) => {
    const lower = form.toLowerCase();
    variants.add(form);
    variants.add(lower);
    variants.add(lower.charAt(0).toUpperCase() + lower.slice(1));
    variants.add(form.toUpperCase());
  });

  return [...variants].some((variant) => speller.correct(variant));
}

export function editDistance(a, b) {
  const rows = a.length;
  const cols = b.length;
  const grid = Array.from({ length: rows + 1 }, () => new Array(cols + 1).fill(0));

  for (let i = 0; i <= rows; i += 1) grid[i][0] = i;
  for (let j = 0; j <= cols; j += 1) grid[0][j] = j;

  for (let i = 1; i <= rows; i += 1) {
    for (let j = 1; j <= cols; j += 1) {
      const substitution = a[i - 1] === b[j - 1] ? 0 : 1;

      grid[i][j] = Math.min(
        grid[i - 1][j] + 1,
        grid[i][j - 1] + 1,
        grid[i - 1][j - 1] + substitution
      );

      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        grid[i][j] = Math.min(grid[i][j], grid[i - 2][j - 2] + TRANSPOSITION_COST);
      }
    }
  }

  return grid[rows][cols];
}

function affixAffinity(a, b) {
  let prefix = 0;
  while (prefix < a.length && prefix < b.length && a[prefix] === b[prefix]) {
    prefix += 1;
  }

  let suffix = 0;
  while (
    suffix < a.length &&
    suffix < b.length &&
    a[a.length - 1 - suffix] === b[b.length - 1 - suffix]
  ) {
    suffix += 1;
  }

  return prefix + suffix;
}

function isLeadingTransposition(a, b) {
  return a.length > 1 && b.length > 1 && a[0] === b[1] && a[1] === b[0];
}

function scoreCandidate(word, candidate) {
  const typed = word.toLowerCase();
  const option = candidate.toLowerCase();

  let cost = editDistance(typed, option);

  if (typed[0] !== option[0] && !isLeadingTransposition(typed, option)) {
    cost += FIRST_LETTER_PENALTY;
  }
  if (candidate !== option && word === typed) {
    cost += CAPITALIZATION_PENALTY;
  }

  return { cost, affinity: affixAffinity(typed, option) };
}

export function rankSuggestions(word, candidates) {
  return candidates
    .map((candidate, index) => ({ candidate, index, ...scoreCandidate(word, candidate) }))
    .sort((a, b) => a.cost - b.cost || b.affinity - a.affinity || a.index - b.index)
    .map((entry) => entry.candidate);
}

export function tokenizeWords(text) {
  const pattern = /[\p{L}\p{M}]+(?:['\u2019][\p{L}\p{M}]+)*/gu;
  const tokens = [];

  let match = pattern.exec(text);
  while (match !== null) {
    tokens.push({ text: match[0], start: match.index, end: match.index + match[0].length });
    match = pattern.exec(text);
  }

  return tokens;
}

export function findSkipSpans(text) {
  const spans = [];

  NON_LANGUAGE_PATTERNS.forEach((source) => {
    const pattern = new RegExp(source.source, source.flags);

    let match = pattern.exec(text);
    while (match !== null) {
      if (match[0].length === 0) {
        pattern.lastIndex += 1;
      } else {
        spans.push({ start: match.index, end: match.index + match[0].length });
      }
      match = pattern.exec(text);
    }
  });

  return spans;
}

function createSuggestionLookup(speller) {
  const cache = new Map();

  return (word) => {
    const key = word.toLowerCase();

    if (!cache.has(key)) {
      cache.set(
        key,
        isSpelledCorrectly(speller, word)
          ? null
          : rankSuggestions(word, speller.suggest(word)).slice(0, MAX_SUGGESTIONS)
      );
    }

    return cache.get(key);
  };
}

export function findMisspelledRanges(speller, text) {
  if (!speller || !text) return [];

  const skipSpans = findSkipSpans(text);
  const suggestionsFor = createSuggestionLookup(speller);
  const ranges = [];

  tokenizeWords(text).forEach((token) => {
    const insideSkipSpan = skipSpans.some(
      (span) => token.start < span.end && token.end > span.start
    );
    if (insideSkipSpan) return;

    const word = token.text.replace(CURLY_APOSTROPHE, "'");
    if (!isCheckable(word)) return;

    const suggestions = suggestionsFor(word);
    if (!suggestions) return;

    ranges.push({ word: token.text, start: token.start, end: token.end, suggestions });
  });

  return ranges;
}

export function replaceRange(text, start, end, replacement) {
  return text.slice(0, start) + replacement + text.slice(end);
}
